/**
 * Storage Keys 관리
 * - 모든 localStorage·sessionStorage 키를 중앙 집중화하여 관리
 * - 타입 안전성 보장
 */
export const STORAGE_KEYS = {
  accessToken: "selechub-accessToken",
  refreshToken: "selechub-refreshToken",
  /**
   * 회원가입 진행 상태(인증을 마친 토큰·이메일과 STEP 1~3 입력값).
   * 인증 링크는 보통 새 탭에서 열리므로 탭 사이에서 이어지도록 localStorage 에 둔다.
   */
  signupSession: "selechub-signup-session",
  /**
   * 인증 메일을 보낸 뒤 수신을 기다리는 상태(회원 유형·이메일·발송 결과).
   * 새로고침해도 대기 화면으로 돌아오도록 두되, 탭을 벗어나면 필요 없으므로 sessionStorage 에 둔다.
   */
  signupPending: "selechub-signup-pending",
  /**
   * 직무 입력 공개 화면(`/job-input/:token`)의 작성 중 초안. 뒤에 `:<토큰>` 을 붙여 링크마다 따로 둔다.
   * 계정 없는 현직자 · 관리자의 개인 업무 서술이라 공용 PC 에 오래 남지 않도록 탭 단위(sessionStorage)로만 둔다.
   */
  jobInputDraft: "selechub-job-input-draft",
  /**
   * 직무 화면(직무분석 · 역량모델 · AI 리터러시 · 입사지원서 · 면접준비)에서 마지막으로 본 직무(73차 DA73-03). 뒤에 `.<워크스페이스 id>` 를
   * 붙인다. 사이드바로 메뉴를 다녀와도 같은 직무가 열리게 탭 단위(sessionStorage)로 두고, 전환 · 로그아웃에서 지운다.
   */
  lastJobId: "selechub.lastJobId",
  /** 면접 콘솔 대상자 필터 4종(73차 DA73-01). 뒤에 `.<워크스페이스 id>` 를 붙인다. 탭 단위, 전환 · 로그아웃에서 지운다. */
  consoleFilters: "selechub.console.filters",
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

/**
 * 인증을 마친 뒤 입력한 값(STEP 1 회사 정보 · STEP 2 조직 유형 · 담당자 역할 · 구성원 목록).
 * 새로고침·탭 복귀에도 이어서 진행하려고 함께 저장한다. 값이 없을 수 있으므로 모두 선택 필드다.
 * STEP 3 이메일 업로드의 미리보기는 담지 않는다(파일은 복원할 수 없다). [확인 완료] 로 담은 목록은 `invites` 에 들어간다.
 * 옛 화면(2026-09-30 설계서 개정 전)이 남긴 `jobField` · `activityArea` · `purposes` 는 저장소에 남아 있을 수 있지만
 * 읽는 쪽이 무시한다.
 * 공통 파일에서 도메인 타입(`SignupStep` 등)을 참조하지 않으려고 문자열로 두고, 읽는 쪽
 * (회원가입 화면)이 알고 있는 목록으로 확인한다.
 *
 * **비밀번호는 저장하지 않는다.** 복원 화면은 비밀번호 두 칸을 비운 채 다시 받는다.
 */
export interface SignupSessionProgress {
  /** 마지막으로 머물던 단계(`SignupStep`) */
  step?: string;
  name?: string;
  companyName?: string;
  industry?: string;
  size?: string;
  template?: string;
  staffRole?: string;
  invites?: { email: string; name: string; role: string }[];
}

/** 회원가입 진행 상태 (가입이 끝나면 지운다) */
export interface SignupSession extends SignupSessionProgress {
  signupToken: string;
  email: string;
  /** 새 가입은 `COMPANY` 뿐이다. `INDIVIDUAL` 은 개정 전 세션을 읽으려고 남겨 두고, 읽는 쪽이 버린다. */
  accountType: "COMPANY" | "INDIVIDUAL";
  /** 가입 제출 토큰의 만료 시각(ISO). 지나면 이메일 인증부터 다시 해야 한다. */
  expiresAt?: string;
}

/**
 * 저장소 접근 자체가 던지는 예외를 삼킨다.
 * `JSON.parse` 실패만 잡으면 부족하다 — 사파리 프라이빗·사이트 데이터 차단·서드파티 iframe 에서는
 * `window.localStorage` 를 건드리는 순간 `SecurityError` 가 난다. 회원가입 인증 랜딩에서 이 예외가 새면
 * 서버는 이미 메일 토큰을 소비한 뒤라 사용자가 링크 만료까지 갇힌다. 읽기는 `null`, 쓰기·삭제는 성공
 * 여부(`boolean`)로 알려 호출하는 화면이 안내를 띄울 수 있게 한다.
 */
function readJson<T>(storage: () => Storage, key: string): T | null {
  try {
    const raw = storage().getItem(key);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as T;
    } catch {
      // 손상된 값은 없는 것으로 본다.
      storage().removeItem(key);
      return null;
    }
  } catch {
    return null;
  }
}

function writeJson(
  storage: () => Storage,
  key: string,
  value: unknown,
): boolean {
  try {
    storage().setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function removeKey(storage: () => Storage, key: string): boolean {
  try {
    storage().removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export const signupSessionStorage = {
  get(): SignupSession | null {
    return readJson<SignupSession>(
      () => window.localStorage,
      STORAGE_KEYS.signupSession,
    );
  },

  /** 저장에 실패하면(저장소가 막힌 브라우저) `false`. 호출하는 화면이 안내를 띄운다. */
  set(session: SignupSession): boolean {
    return writeJson(
      () => window.localStorage,
      STORAGE_KEYS.signupSession,
      session,
    );
  },

  clear(): boolean {
    return removeKey(() => window.localStorage, STORAGE_KEYS.signupSession);
  },
} as const;

/**
 * 인증 메일 발송 뒤 수신 대기 상태.
 * `sent` 는 발송 응답(`SignupEmailResponse`)을 그대로 담는다. 공통 파일에서 도메인 타입을
 * 참조하지 않으려고 `unknown` 으로 두고, 읽는 쪽(회원가입 화면)이 모양을 확인한다.
 */
export interface SignupPending {
  accountType: "COMPANY" | "INDIVIDUAL";
  email: string;
  sent: unknown;
}

/**
 * 회원가입 수신 대기 상태 (탭 단위, 인증을 마치거나 처음부터 다시 시작하면 지운다)
 * - 인증 링크는 서버에 그대로 살아 있으므로 새로고침 뒤에도 다시 보낼 필요가 없다.
 */
export const signupPendingStorage = {
  get(): SignupPending | null {
    return readJson<SignupPending>(
      () => window.sessionStorage,
      STORAGE_KEYS.signupPending,
    );
  },

  /** 대기 화면은 편의 기능이라 저장 실패를 막지 않는다(실패해도 서버의 링크는 살아 있다). */
  set(pending: SignupPending): boolean {
    return writeJson(
      () => window.sessionStorage,
      STORAGE_KEYS.signupPending,
      pending,
    );
  },

  clear(): boolean {
    return removeKey(() => window.sessionStorage, STORAGE_KEYS.signupPending);
  },
} as const;

/**
 * 직무 입력 공개 화면의 작성 중 초안(57차 AG57-04).
 * 공통 파일에서 도메인 타입(`InterviewAnswer`)을 참조하지 않으려고 같은 모양을 여기 다시 적는다.
 */
export interface JobInputDraft {
  tasksText: string;
  answers: { question: string; answer: string }[];
}

/**
 * 직무 입력 초안 (탭 단위, 제출에 성공하거나 이미 제출된 링크로 확인되면 지운다)
 * - 새로고침 · 실수로 닫았다 같은 탭에서 다시 연 경우에 되살린다. 저장 실패는 입력을 막지 않는다.
 */
export const jobInputDraftStorage = {
  get(token: string): JobInputDraft | null {
    return readJson<JobInputDraft>(
      () => window.sessionStorage,
      `${STORAGE_KEYS.jobInputDraft}:${token}`,
    );
  },

  set(token: string, draft: JobInputDraft): boolean {
    return writeJson(
      () => window.sessionStorage,
      `${STORAGE_KEYS.jobInputDraft}:${token}`,
      draft,
    );
  },

  clear(token: string): boolean {
    return removeKey(
      () => window.sessionStorage,
      `${STORAGE_KEYS.jobInputDraft}:${token}`,
    );
  },
} as const;

/** 워크스페이스별 키(`<접두>.<워크스페이스 id>`). 워크스페이스가 정해지지 않았으면(0) 쓰지 않는다. */
function workspaceKey(prefix: string, workspaceId: number): string | null {
  return Number.isInteger(workspaceId) && workspaceId > 0
    ? `${prefix}.${String(workspaceId)}`
    : null;
}

/** 이 탭의 sessionStorage 에서 `<접두>.` 로 시작하는 키를 모두 지운다(워크스페이스 전환 · 로그아웃). */
function removePrefixed(prefix: string): void {
  try {
    const storage = window.sessionStorage;
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(`${prefix}.`)) keys.push(key);
    }
    keys.forEach((key) => {
      storage.removeItem(key);
    });
  } catch {
    // 저장소가 막힌 브라우저. 저장된 값도 없다.
  }
}

/**
 * 마지막으로 본 직무를 기억한다(73차 DA73-03). 각 직무 화면이 `?jobId` 를 replace 로 맞추는 자리에서 부른다.
 * 저장 실패(프라이빗 모드 등)는 조용히 넘긴다 — 기억은 편의 기능이라 화면을 막지 않는다.
 */
export function rememberLastJobId(workspaceId: number, jobId: number): void {
  const key = workspaceKey(STORAGE_KEYS.lastJobId, workspaceId);
  if (!key || !Number.isInteger(jobId) || jobId <= 0) return;
  writeJson(() => window.sessionStorage, key, jobId);
}

/**
 * 기억한 직무 id(없거나 읽지 못하면 null). 첫 선택 순서는 `?jobId` → 이 값(**지금 목록에 있을 때만** — 없으면 안내 없이 다음 순서)
 * → 화면의 기본값이다. 목록에 있는지는 부르는 화면이 확인한다.
 */
export function recallLastJobId(workspaceId: number): number | null {
  const key = workspaceKey(STORAGE_KEYS.lastJobId, workspaceId);
  if (!key) return null;
  const value = readJson<unknown>(() => window.sessionStorage, key);
  return typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : null;
}

/** 기억한 직무를 모든 워크스페이스에서 지운다 — `forgetWorkspaceViewState` 안에서만 부른다(73차 PR73-11, export 아님). */
function forgetLastJobIds(): void {
  removePrefixed(STORAGE_KEYS.lastJobId);
}

/**
 * 면접 콘솔 대상자 필터 · 보던 면접(73차 DA73-01). 공통 파일에서 도메인 타입(`InterviewProgress`)을 참조하지 않으려고 문자열로 두고, 읽는 쪽(콘솔)이
 * 알고 있는 값인지 확인한다. 모양이 다른 칸은 읽을 때 버린다.
 */
export interface ConsoleFilters {
  job?: string;
  progress?: string;
  keyword?: string;
  mineOnly?: boolean;
  /** 보던 면접 세션 id — 사이드바로 메뉴를 다녀오면 `?open` 쿼리가 사라지므로 여기서 되살린다. */
  openSessionId?: number;
}

export const consoleFiltersStorage = {
  get(workspaceId: number): ConsoleFilters | null {
    const key = workspaceKey(STORAGE_KEYS.consoleFilters, workspaceId);
    if (!key) return null;
    const raw = readJson<Record<string, unknown>>(
      () => window.sessionStorage,
      key,
    );
    if (!raw || typeof raw !== "object") return null;
    const filters: ConsoleFilters = {};
    if (typeof raw.job === "string") filters.job = raw.job;
    if (typeof raw.progress === "string") filters.progress = raw.progress;
    if (typeof raw.keyword === "string") filters.keyword = raw.keyword;
    if (typeof raw.mineOnly === "boolean") filters.mineOnly = raw.mineOnly;
    if (
      typeof raw.openSessionId === "number" &&
      Number.isInteger(raw.openSessionId) &&
      raw.openSessionId > 0
    ) {
      filters.openSessionId = raw.openSessionId;
    }
    return filters;
  },

  /** 저장 실패는 화면을 막지 않는다(새로고침하면 기본 필터로 돌아갈 뿐이다). */
  set(workspaceId: number, filters: ConsoleFilters): boolean {
    const key = workspaceKey(STORAGE_KEYS.consoleFilters, workspaceId);
    if (!key) return false;
    return writeJson(() => window.sessionStorage, key, filters);
  },

  clear(workspaceId: number): boolean {
    const key = workspaceKey(STORAGE_KEYS.consoleFilters, workspaceId);
    if (!key) return false;
    return removeKey(() => window.sessionStorage, key);
  },
} as const;

/**
 * 워크스페이스에 묶인 화면 기억(마지막 직무 · 콘솔 필터)을 모두 지운다. 워크스페이스 전환(`applyWorkspaceSwitch`)과 로그인 화면 이동
 * (`goLoginPage` — 로그아웃 · 세션 만료가 모두 지난다)에서 부른다.
 */
export function forgetWorkspaceViewState(): void {
  forgetLastJobIds();
  removePrefixed(STORAGE_KEYS.consoleFilters);
}

/**
 * Token Storage 유틸리티
 * - JWT 토큰 저장/조회/삭제를 위한 추상화 계층
 */
function readText(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeText(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // 저장소가 막힌 브라우저. 토큰은 메모리(스토어)에 남아 이 탭 안에서는 계속 쓸 수 있다.
  }
}

export const tokenStorage = {
  getAccessToken(): string | null {
    return readText(STORAGE_KEYS.accessToken);
  },

  getRefreshToken(): string | null {
    return readText(STORAGE_KEYS.refreshToken);
  },

  setAccessToken(token: string): void {
    writeText(STORAGE_KEYS.accessToken, token);
  },

  setRefreshToken(token: string): void {
    writeText(STORAGE_KEYS.refreshToken, token);
  },

  removeAccessToken(): void {
    removeKey(() => window.localStorage, STORAGE_KEYS.accessToken);
  },

  removeRefreshToken(): void {
    removeKey(() => window.localStorage, STORAGE_KEYS.refreshToken);
  },

  clearTokens(): void {
    this.removeAccessToken();
    this.removeRefreshToken();
  },

  setTokens(accessToken: string, refreshToken: string): void {
    this.setAccessToken(accessToken);
    this.setRefreshToken(refreshToken);
  },
} as const;
